// EditPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DataService } from 'src/services/data-service';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid
} from '@mui/material';
import _ from 'lodash';

import Loader from 'src/components/Loader';
import GenericInput from 'src/components/input-components/GenericTextInput';
import Repeater from 'src/components/repeater';

import { IPageState, initialIPageState } from '../../PageWidgetSection/page';
import { validateSeoField, validateRepeatorField } from '../../PageWidgetSection/validation';
import { validateForm } from '../../PageWidgetSection/submission';

const EditPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<IPageState>(_.cloneDeep(initialIPageState));
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const res = await DataService.get(`api/page/get?id=${id}`);
      if (!res.ok) {
        toast.error('Failed to load page data');
        setLoading(false);
        return;
      }
      const data = await res.json();

      // Parse config JSON and map widget items
      let parsedConfig: { SEO?: any; Items?: any[] } = { SEO: {}, Items: [] };
      try {
        parsedConfig = JSON.parse(data.config || '{}');
        parsedConfig.Items = parsedConfig.Items.map((w) => {
          const { data, ...rest } = w;
          return { ...rest, ...data };
        });
      } catch (err) {
        console.error('Error parsing config JSON:', err);
        parsedConfig = { SEO: {}, Items: [] };
      }

      setPageState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          Id: data.id || '',
          Active: data.active, // store active status (boolean)
          SEO: {
            Title: parsedConfig.SEO?.Title || data.title || '',
            Description: parsedConfig.SEO?.Description || data.description || '',
            Keywords: parsedConfig.SEO?.Keywords || data.keywords || '',
            Code: parsedConfig.SEO?.Code || data.code || '' // route/code field
          },
          Items: parsedConfig.Items || []
        }
      }));
    } catch (error) {
      console.error('Network error:', error);
      toast.error('Network error retrieving page data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, [id]);

  // ---------------------------------------------------------------------
  // HANDLERS FOR SEO FIELDS
  // ---------------------------------------------------------------------
  const onFieldChange = (fieldName: keyof IPageState['values']['SEO'], value: any) => {
    const newState = _.cloneDeep(pageState);
    newState.values.SEO[fieldName] = value;
    newState.touched.SEO[fieldName] = true;
    newState.errors.SEO[fieldName] = validateSeoField(fieldName, value);
    setPageState(newState);
  };

  const onFieldBlur = (fieldName: keyof IPageState['values']['SEO']) => {
    const newState = _.cloneDeep(pageState);
    newState.touched.SEO[fieldName] = true;
    newState.errors.SEO[fieldName] = validateSeoField(fieldName, newState.values.SEO[fieldName]);
    setPageState(newState);
  };

  // ---------------------------------------------------------------------
  // HANDLERS FOR REPETITION (WIDGET ITEMS)
  // ---------------------------------------------------------------------
  const onRepeaterFieldChange = (index: number, newItem: any, fieldName: string) => {
    const newState = _.cloneDeep(pageState);
    newState.values.Items[index] = newItem;
    if (!newState.touched.Items[index]) {
      newState.touched.Items[index] = {};
    }
    newState.touched.Items[index][fieldName] = true;
    if (!newState.errors.Items[index]) {
      newState.errors.Items[index] = {};
    }
    newState.errors.Items[index][fieldName] = validateRepeatorField(fieldName, newItem, newItem[fieldName]);
    setPageState(newState);
  };

  const onRepeaterFieldBlur = (index: number, fieldName: string) => {
    const newState = _.cloneDeep(pageState);
    if (!newState.touched.Items[index]) {
      newState.touched.Items[index] = {};
    }
    newState.touched.Items[index][fieldName] = true;
    setPageState(newState);
  };

  const addItem = () => {
    const newState = _.cloneDeep(pageState);
    newState.values.Items.push({ code: '', type: '' as any });
    setPageState(newState);
  };

  const deleteItem = (index: number) => {
    const newState = _.cloneDeep(pageState);
    newState.values.Items = newState.values.Items.filter((_, i) => i !== index);
    setPageState(newState);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const { errors, touched, isValid } = validateForm(pageState.values);
    const newState = _.cloneDeep(pageState);
    newState.errors = errors;
    setPageState(newState);
    console.log('Global validation result:', isValid, errors);

    if (!isValid) {
      setSubmitting(false);
      toast.error('Please fix the validation errors before updating.');
      return;
    }

    const mappedItems = pageState.values.Items.map((widget) => {
      const { type, code, ...rest } = widget as any;
      return { type, code, data: rest };
    });

    try {
      const payload = {
        Code: pageState.values.SEO.Code,
        Title: pageState.values.SEO.Title,
        Description: pageState.values.SEO.Description,
        Config: JSON.stringify({
          SEO: pageState.values.SEO,
          Items: mappedItems
        }),
        Active: pageState.values.Active
      };

      const response = await DataService.post(`api/page/update/${pageState.values.Id}`, payload);
      if (!response.ok) {
        const errorMessage = await response.text();
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      toast.success('Page updated successfully.');
      setSuccess(true);
      navigate(`/web-builder/pages/edit/${id}`);
    } catch (err) {
      console.error('Error updating page:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicatePage = async () => {
    setSubmitting(true);
    setError(null);

    const originalCode = pageState.values.SEO.Code || 'new-page';
    const newCode = `${originalCode}-${Date.now()}`.toLowerCase();
    const originalTitle = pageState.values.SEO.Title || 'Untitled Page';
    const newTitle = `${originalTitle} (Copy)`;

    const duplicatedItems = pageState.values.Items.map(({ type, code, ...rest }) => ({
      type: type || 'UNKNOWN_WIDGET',
      code: code || 'Unknown Section',
      data: rest || {}
    }));

    const payload = {
      Title: newTitle,
      Code: newCode,
      Config: JSON.stringify({
        SEO: { ...pageState.values.SEO, Code: newCode },
        Items: duplicatedItems
      }),
      Active: false
    };

    try {
      const response = await DataService.post('api/page/create', payload);
      if (!response.ok) {
        const errorMessage = await response.text();
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      const newPage = await response.json();
      toast.success('Duplicate Page Created Successfully');
      navigate(`/web-builder/pages/edit/${newPage.id}`);
    } catch (err) {
      console.error('Error duplicating page:', err);
      toast.error('Failed to duplicate page. Please check the fields.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------
  // TOGGLE ACTIVE STATUS HANDLER (Activate/Deactivate)
  // ---------------------------------------------------------------------
  const handleToggleActive = async () => {
    setSubmitting(true);
    try {
      const endpoint = pageState.values.Active
        ? `api/page/deactivate?id=${pageState.values.Id}`
        : `api/page/activate?id=${pageState.values.Id}`;
      const response = await DataService.patch(endpoint, {});
      if (!response.ok) {
        const errorMessage = await response.text();
        toast.error(errorMessage);
        setSubmitting(false);
        return;
      }
      // Toggle the active flag in state
      setPageState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          Active: !prev.values.Active,
        },
      }));
      toast.success(`Page ${pageState.values.Active ? 'deactivated' : 'activated'} successfully.`);
    } catch (error) {
      console.error('Error toggling page status:', error);
      toast.error('Failed to toggle page status.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader size={75} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" gutterBottom>Edit Page</Typography>
            <Button
              variant="contained"
              color="primary"
              href={`https://construction.mec-kin.com/${pageState.values.SEO.Code}`}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!pageState.values.SEO.Code}
            >
              View Page
            </Button>
          </Box>

          {/* SEO Section */}
          <Card elevation={6} sx={{ p: 2 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                SEO Section
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <GenericInput
                    type="text"
                    name="Title"
                    title="Title"
                    value={pageState.values.SEO.Title}
                    onChange={(val) => onFieldChange('Title', val)}
                    onBlur={() => onFieldBlur('Title')}
                    error={pageState.errors.SEO.Title}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericInput
                    type="text"
                    name="Keywords"
                    title="Keywords"
                    value={pageState.values.SEO.Keywords}
                    onChange={(val) => onFieldChange('Keywords', val)}
                    onBlur={() => onFieldBlur('Keywords')}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <GenericInput
                    type="text"
                    name="Code"
                    title="Route"
                    value={pageState.values.SEO.Code}
                    onChange={(val) => onFieldChange('Code', val)}
                    onBlur={() => onFieldBlur('Code')}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12}>
                  <GenericInput
                    type="text"
                    name="Description"
                    title="Description"
                    value={pageState.values.SEO.Description}
                    onChange={(val) => onFieldChange('Description', val)}
                    onBlur={() => onFieldBlur('Description')}
                    disabled={submitting}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Widgets Section */}
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <Card>
              <CardContent>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Typography variant="h5" gutterBottom>Page Content</Typography>
                  <Repeater
                    items={pageState.values.Items}
                    errors={pageState.errors.Items}
                    addItem={addItem}
                    deleteItem={deleteItem}
                    updateItem={onRepeaterFieldChange}
                    blurItem={onRepeaterFieldBlur}
                  />
                </Box>
              </CardContent>
            </Card>
          </Container>

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ mr: 1 }}
              >
                {submitting ? 'Updating...' : 'Update Page'}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleDuplicatePage}
                disabled={submitting}
              >
                {submitting ? 'Duplicating...' : 'Duplicate Page'}
              </Button>
            </Box>
            <Box>
              <Button
                variant="contained"
                color={pageState.values.Active ? 'error' : 'success'}
                onClick={handleToggleActive}
                disabled={submitting}
              >
                {submitting
                  ? pageState.values.Active
                    ? 'Deactivating...'
                    : 'Activating...'
                  : pageState.values.Active
                  ? 'Deactivate Page'
                  : 'Activate Page'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EditPage;
